import { useState } from 'react'
import { useParams, Link } from 'react-router'
import { formatDistanceToNow, format } from 'date-fns'
import { useAdminUserDetail, useUpdateUserTier, useSuspendUser, useUnsuspendUser, useSetInternalAccess, useSetIsTest, useImpersonateUser } from '@/hooks/useAdminUsers'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  ArrowLeft, Copy, MapPin, Layout, Ban, ShieldOff,
  ExternalLink, Loader2, ShieldCheck, KeyRound, Gift, FlaskConical,
  Building2, Box, ChevronRight, Download,
} from 'lucide-react'
import { useExportLocation } from '@/hooks/useAdminLocations'
import { ThumbnailPreview } from '@/components/shared/ThumbnailPreview'
import { toast } from 'sonner'
import type { AdminUserLayout, InternalAccessTier } from '@/lib/api/admin-users'
import { formatSceneCounts } from '@/lib/layoutRequirements'
import { adminTierLabel, formatCap, formatLayoutCaps, resolveAccountAccess, TIER_LABELS } from '@/lib/tierLadder'

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const { data, isLoading } = useAdminUserDetail(userId)
  const updateTier = useUpdateUserTier()
  const suspend = useSuspendUser()
  const unsuspend = useUnsuspendUser()
  const setInternalAccess = useSetInternalAccess()
  const setIsTest = useSetIsTest()
  const impersonate = useImpersonateUser()
  const exportLocation = useExportLocation()

  const [internalTier, setInternalTier] = useState<InternalAccessTier>('pro')

  const [tierDialogOpen, setTierDialogOpen] = useState(false)
  const [newTier, setNewTier] = useState('')
  const [tierReason, setTierReason] = useState('')

  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false)
  const [suspendReason, setSuspendReason] = useState('')

  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false)
  const [impersonateReason, setImpersonateReason] = useState('')
  const [impersonateLink, setImpersonateLink] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    )
  }

  if (!data) {
    return <p className="text-muted-foreground">User not found.</p>
  }

  const { user, profile, locations, layouts, gift_licenses } = data
  const isBanned = !!user.banned_until && new Date(user.banned_until) > new Date()
  const hasInternalAccess = !!user.app_metadata?.internal_access
  const isTestUser = (profile as Record<string, unknown>)?.is_test === true
  const stripeId = (profile as Record<string, unknown>)?.stripe_customer_id as string | null
  const internalAccess = (user.app_metadata?.internal_access as string | undefined) ?? null
  const previewUntil = (profile as Record<string, unknown>)?.legacy_business_preview_until as string | null
  const access = resolveAccountAccess({
    subscriptionTier: (profile as Record<string, unknown>)?.subscription_tier as string | undefined,
    internalAccess,
    legacyCaps: (profile as Record<string, unknown>)?.legacy_caps === true,
    previewUntil,
  })

  function copyId() {
    navigator.clipboard.writeText(user.id)
    toast.success('User ID copied')
  }

  return (
    <div>
      <div className="mb-4">
        <Link to="/users" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Users
        </Link>
      </div>

      <PageHeader
        title={((profile as Record<string, unknown>)?.display_name as string) || user.email || 'Unknown User'}
        description={user.email}
        actions={
          <div className="flex items-center gap-2">
            {isTestUser && (
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                Test user
              </Badge>
            )}
            {isBanned && <Badge variant="destructive">Suspended</Badge>}
          </div>
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">User ID</Label>
              <div className="flex items-center gap-1">
                <code className="text-xs">{user.id.slice(0, 12)}...</code>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={copyId}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">Email</Label>
              <span className="text-sm">{user.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">Signed Up</Label>
              <span className="text-sm">{format(new Date(user.created_at), 'MMM d, yyyy')}</span>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">Last Sign-in</Label>
              <span className="text-sm">
                {user.last_sign_in_at
                  ? formatDistanceToNow(new Date(user.last_sign_in_at), { addSuffix: true })
                  : 'Never'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Subscription</CardTitle>
            <CardDescription>Manage tier and billing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">Tier</Label>
              <div className="flex items-center gap-2">
                <Badge>{adminTierLabel((profile as Record<string, unknown>)?.subscription_tier as string | undefined)}</Badge>
                <Button variant="outline" size="sm" onClick={() => {
                  setNewTier((profile as Record<string, unknown>)?.subscription_tier as string ?? 'trial')
                  setTierDialogOpen(true)
                }}>
                  Override
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">Status</Label>
              <Badge variant="secondary">
                {(profile as Record<string, unknown>)?.subscription_status as string ?? 'none'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-muted-foreground">Billing Cycle</Label>
              <span className="text-sm">
                {(profile as Record<string, unknown>)?.billing_cycle as string ?? '—'}
              </span>
            </div>
            {internalAccess && (
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground">Internal access</Label>
                <Badge
                  variant="outline"
                  className="border-purple-300 bg-purple-50 text-purple-800"
                  title={`app_metadata.internal_access = '${internalAccess}' — overrides the subscription tier, bypasses Stripe and past-due`}
                >
                  {adminTierLabel(internalAccess)}
                </Badge>
              </div>
            )}
            {/* Grandfather flag — explains why a subscriber's caps differ from
                their tier's published limits. Support needs this visible. */}
            {!!(profile as Record<string, unknown>)?.legacy_caps && (
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground">Caps</Label>
                <Badge variant="secondary" title="Subscribed before the Business tier launched — exempt from the per-layout caps, the building area cap, and the Business aircraft classes">
                  Legacy (grandfathered)
                </Badge>
              </div>
            )}
            {/* 30-day Business preview granted to legacy Pro at the Business launch —
                explains why a Pro account can place T-rows / airport buildings. */}
            {!!(profile as Record<string, unknown>)?.legacy_business_preview_until && (() => {
              const until = new Date((profile as Record<string, unknown>).legacy_business_preview_until as string)
              const open = until.getTime() > Date.now()
              return (
                <div className="flex items-center justify-between">
                  <Label className="text-muted-foreground">Business preview</Label>
                  <Badge variant={open ? 'default' : 'secondary'} title="Every Business feature is open for this legacy Pro until this date; caps stay unlimited afterwards">
                    {open ? 'Open until' : 'Closed'} {until.toLocaleDateString()}
                  </Badge>
                </div>
              )
            })()}
            {/* Trial / Funnel Status */}
            {(profile as Record<string, unknown>)?.subscription_tier === 'trial' && (
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground">Trial Status</Label>
                {(() => {
                  const trialStartedRaw = (profile as Record<string, unknown>)?.trial_started_at as string | null
                  if (!trialStartedRaw) {
                    return <Badge variant="secondary">Not started (no save yet)</Badge>
                  }
                  const trialStarted = new Date(trialStartedRaw)
                  const threeDaysLater = new Date(trialStarted.getTime() + 3 * 24 * 60 * 60 * 1000)
                  const isExpired = new Date() > threeDaysLater
                  return isExpired ? (
                    <Badge variant="destructive">Frozen (trial expired)</Badge>
                  ) : (
                    <Badge className="bg-yellow-100 text-yellow-800">
                      Active — expires {formatDistanceToNow(threeDaysLater, { addSuffix: true })}
                    </Badge>
                  )
                })()}
              </div>
            )}
            {stripeId && (
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground">Stripe</Label>
                <a
                  href={`https://dashboard.stripe.com/customers/${stripeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* What the app resolves for this account — internal access, legacy caps
            and the Business preview folded in (mirrors deriveSubscription). */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Effective access</CardTitle>
            <CardDescription>What the app enforces for this account right now</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <AccessStat
              label="Plan tier"
              value={TIER_LABELS[access.tier]}
              hint={access.source === 'internal_access' ? 'via internal access' : access.legacyCaps ? 'legacy caps' : undefined}
            />
            <AccessStat
              label="Feature tier"
              value={TIER_LABELS[access.featureTier]}
              hint={access.previewOpen && previewUntil ? `Business preview until ${format(new Date(previewUntil), 'MMM d, yyyy')}` : undefined}
            />
            <AccessStat label="Per layout" value={formatLayoutCaps(access.layoutCaps)} />
            <AccessStat label="Building area" value={`${formatCap(access.maxBuildingAreaSqft)} sq ft`} hint="per building" />
            <AccessStat label="Locations" value={`${locations.length} / ${formatCap(access.maxLocations)}`} />
          </CardContent>
        </Card>

        {/* Locations Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Locations ({locations.length})
            </CardTitle>
            <CardDescription>Where this user has set up hangars</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {locations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No locations yet.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {locations.map((loc) => (
                  <Link
                    key={loc.id}
                    to={`/locations/${loc.id}`}
                    className="block overflow-hidden rounded-md border transition-shadow hover:shadow-md"
                  >
                    <ThumbnailPreview
                      src={loc.thumbnail_url}
                      alt={loc.name}
                      kind="location"
                      className="h-28 w-full rounded-none"
                      iconClassName="h-6 w-6"
                    />
                    <div className="space-y-1 p-3">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">{loc.name}</span>
                        {!!loc.airport_code && (
                          <Badge variant="secondary" className="text-[10px]">{loc.airport_code}</Badge>
                        )}
                      </div>
                      {loc.latitude != null && loc.longitude != null && (
                        <p className="text-xs text-muted-foreground">
                          {Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          {loc.layout_count} layout{loc.layout_count !== 1 ? 's' : ''}
                        </p>
                        {/* Inside a Link — swallow the navigation so the click only exports. */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Export location + all layouts (JSON)"
                          disabled={exportLocation.isPending}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            exportLocation.mutate({ locationId: loc.id })
                          }}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Layouts Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Layout className="h-4 w-4" /> Layouts ({layouts.length})
            </CardTitle>
            <CardDescription>Each stage links to its full detail (items, JSON, 3D preview)</CardDescription>
          </CardHeader>
          <CardContent>
            {layouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No layouts yet.</p>
            ) : (
              <ul className="divide-y">
                {layouts.map((layout) => (
                  <li key={layout.id}>
                    <Link
                      to={`/layouts/${layout.id}`}
                      className="flex items-center gap-3 py-3 transition-colors hover:bg-muted/50"
                    >
                      <ThumbnailPreview
                        src={layout.thumbnail_url}
                        alt={layout.name}
                        kind="layout"
                        className="h-11 w-16"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{layout.name}</span>
                          {layout.is_public && <Badge variant="secondary" className="text-[10px]">Public</Badge>}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="h-3 w-3" /> {formatSceneCounts(layout)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Box className="h-3 w-3" /> {layout.item_count} item{layout.item_count !== 1 ? 's' : ''}
                          </span>
                          {layout.item_summary.length > 0 && (
                            <span className="truncate">{formatItemSummary(layout.item_summary)}</span>
                          )}
                          <span>· {formatDistanceToNow(new Date(layout.updated_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Admin Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {/* Internal access — grants a tier without Stripe (app_metadata.internal_access) */}
            {hasInternalAccess ? (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => setInternalAccess.mutate({ userId: user.id, enable: false })}
                disabled={setInternalAccess.isPending}
              >
                {setInternalAccess.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <ShieldCheck className="mr-2 h-4 w-4" />}
                Revoke Internal Access ({adminTierLabel(internalAccess)})
              </Button>
            ) : (
              <div className="flex gap-2">
                <Select value={internalTier} onValueChange={(v) => setInternalTier(v as InternalAccessTier)}>
                  <SelectTrigger className="w-32" aria-label="Internal access tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pilot">Pilot</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setInternalAccess.mutate({ userId: user.id, enable: true, tier: internalTier })}
                  disabled={setInternalAccess.isPending}
                >
                  {setInternalAccess.isPending
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <ShieldCheck className="mr-2 h-4 w-4" />}
                  Grant Internal Access
                </Button>
              </div>
            )}

            {/* Test user flag — excludes from default reports/dashboards */}
            <Button
              variant={isTestUser ? 'secondary' : 'outline'}
              className="w-full"
              onClick={() => setIsTest.mutate({ userId: user.id, isTest: !isTestUser })}
              disabled={setIsTest.isPending}
            >
              {setIsTest.isPending
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <FlaskConical className="mr-2 h-4 w-4" />}
              {isTestUser ? 'Unmark as Test User' : 'Mark as Test User'}
            </Button>

            {isBanned ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => unsuspend.mutate({ userId: user.id, reason: 'Admin unsuspend' })}
                disabled={unsuspend.isPending}
              >
                {unsuspend.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-2 h-4 w-4" />}
                Unsuspend User
              </Button>
            ) : (
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => setSuspendDialogOpen(true)}
              >
                <Ban className="mr-2 h-4 w-4" />
                Suspend User
              </Button>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setImpersonateReason('')
                setImpersonateLink(null)
                setImpersonateDialogOpen(true)
              }}
            >
              <KeyRound className="mr-2 h-4 w-4" />
              Sign in as User…
            </Button>
          </CardContent>
        </Card>

        {/* Gift Licenses */}
        {gift_licenses && gift_licenses.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Gift className="h-4 w-4" /> Gift Licenses ({gift_licenses.length})
              </CardTitle>
              <CardDescription>Bought or redeemed by this user</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {gift_licenses.map((g) => {
                  const r = g as Record<string, unknown>
                  const code = String(r.code ?? '')
                  const tier = String(r.tier ?? '')
                  const days = Number(r.duration_days ?? 0)
                  const redeemed = !!r.redeemed_by
                  const expired = r.expires_at ? new Date(r.expires_at as string) < new Date() : false
                  const status = redeemed ? 'redeemed' : expired ? 'expired' : 'pending'
                  const isBuyer = r.buyer_user_id === user.id
                  return (
                    <li key={String(r.id)} className="flex items-center justify-between gap-2 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {code.slice(0, 4)}…{code.slice(-4)}
                        </code>
                        <Badge variant="secondary" className="text-[10px]">{isBuyer ? 'Bought' : 'Redeemed'}</Badge>
                        {tier && (
                          <Badge variant="outline" className="text-[10px] capitalize">
                            {tier.replace('_', ' ')}{days ? ` · ${days}d` : ''}
                          </Badge>
                        )}
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          status === 'redeemed'
                            ? 'border-green-300 bg-green-50 text-green-800'
                            : status === 'expired'
                              ? 'border-yellow-300 bg-yellow-50 text-yellow-900'
                              : ''
                        }
                      >
                        {status}
                      </Badge>
                    </li>
                  )
                })}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tier Override Dialog */}
      <Dialog open={tierDialogOpen} onOpenChange={setTierDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override Subscription Tier</DialogTitle>
            <DialogDescription>
              This will change the user's tier and associated limits. The change is logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Tier</Label>
              <Select value={newTier} onValueChange={setNewTier}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="pilot">Pilot</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Input
                placeholder="Why is this override needed?"
                value={tierReason}
                onChange={(e) => setTierReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTierDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                updateTier.mutate({ userId: user.id, tier: newTier, reason: tierReason })
                setTierDialogOpen(false)
              }}
              disabled={updateTier.isPending}
            >
              {updateTier.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Dialog */}
      <Dialog open={impersonateDialogOpen} onOpenChange={setImpersonateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign in as user</DialogTitle>
            <DialogDescription>
              Opens the app in a new tab signed in as <strong>{user.email}</strong>. A banner stays
              pinned the whole time, and <strong>Exit impersonation</strong> brings you straight back
              here. Your admin session is unaffected. Action is recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          {!impersonateLink ? (
            <>
              <div className="space-y-2">
                <Label>Reason (optional)</Label>
                <Input
                  autoFocus
                  placeholder="e.g. debugging user-reported issue #123, support escalation…"
                  value={impersonateReason}
                  onChange={(e) => setImpersonateReason(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImpersonateDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => {
                    impersonate.mutate(
                      {
                        userId: user.id,
                        reason: impersonateReason,
                        returnUrl: `${window.location.origin}${window.location.pathname}`,
                      },
                      {
                        onSuccess: (data) => {
                          setImpersonateLink(data.action_link)
                          // Open immediately in a new tab — no copy/paste needed.
                          window.open(data.action_link, '_blank')
                        },
                      },
                    )
                  }}
                  disabled={impersonate.isPending}
                >
                  {impersonate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Generate link
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-3">
                <p className="rounded border border-green-300 bg-green-50 p-3 text-xs text-green-900">
                  Opened in a new tab. If it didn't open, use <strong>Open as user</strong> below.
                  This is a one-time link — it won't be shown again.
                </p>
                <div className="space-y-2">
                  <Label>Sign-in link</Label>
                  <div className="flex gap-2">
                    <Input value={impersonateLink} readOnly className="font-mono text-xs" />
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(impersonateLink)
                        toast.success('Link copied')
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImpersonateDialogOpen(false)}>Close</Button>
                <Button
                  onClick={() => {
                    window.open(impersonateLink, '_blank')
                  }}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open as user
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend User</DialogTitle>
            <DialogDescription>
              This will immediately prevent the user from signing in. Their data will be preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input
              placeholder="Reason for suspension..."
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                suspend.mutate({ userId: user.id, reason: suspendReason })
                setSuspendDialogOpen(false)
              }}
              disabled={suspend.isPending}
            >
              Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function AccessStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

/** Compact friendly item summary, e.g. "3× Cessna 172, 1× SR22 · +2 more". */
function formatItemSummary(summary: AdminUserLayout['item_summary'], max = 3): string {
  if (summary.length === 0) return ''
  const shown = summary.slice(0, max).map((s) => `${s.count}× ${s.name}`).join(', ')
  const extra = summary.length - max
  return extra > 0 ? `${shown} · +${extra} more` : shown
}
